import { toast } from '@/hooks/use-toast';
import type { BulkStockDirection } from '@threadheads/shared';
import { Product, NewProduct } from '../types';
import { inventoryApi, ProcessResult } from '../api/inventoryApi';
import { useInventoryQueries, type InventoryWhoInput } from './useInventoryQueries';
import { useInventoryMutations } from './useInventoryMutations';
import { useRole } from '@/hooks/use-role';

/**
 * Main inventory hook — orchestrates queries and mutations.
 *
 * # IDs not strings (BLU-3xx Phase 47.01.2-B-fix3.1)
 *
 * The single-row mutation methods on this hook (`addStock`, `setStock`,
 * `deductStock`, `moveToUnavailable`, `moveToAvailable`) take a resolved
 * `Product` object — NOT a SKU string.
 *
 * Why: pre-fix3.1, these methods accepted `sku: string` and re-resolved it
 * via `products.find(p => p.sku === sku)`. That lookup ran against the
 * OWN-ORG `products` list, so cross-org SKUs (selected from a connected
 * client's catalog via `useTargetCatalog` in the modals) silently failed
 * with "Product not found" — the dropdown could SHOW the SKU but the
 * submit handler couldn't FIND it. The fix is to thread the resolved
 * `Product` (with its `id`) all the way from the modal down to the
 * mutation, eliminating the round-trip through string.
 *
 * If you find yourself adding a new `*Stock` method or entry point: take
 * the `Product` (or at minimum its `id`) directly. Do NOT re-introduce
 * the `products.find` pattern. The bulk-CSV path (`bulkAddStockFromCsv`,
 * `addStockFn` in `inventoryApi.ts`) is a deliberate exception — CSVs
 * are inherently keyed by SKU string and the resolution happens
 * server-side via the cross-org-aware backend resolver (B-06).
 *
 * `options.enabled` (BLU-1189) is passed straight through to
 * `useInventoryQueries` — a mounted-but-closed dialog that only wants the
 * mutation wrappers gates it on its own open flag so it never opens a second
 * catalogue subscription. Note the mutations stay live either way; only the
 * read is gated, so `products` is empty (and `loading` true) until it opens.
 */
export function useInventory(
  productionType?: 'PAP' | 'POD' | 'TRIM',
  options?: { enabled?: boolean; who?: InventoryWhoInput },
) {
  // Get query state
  const {
    products,
    loading,
    error,
    refreshInventory,
    selectedLocation,
    isFetching,
  } = useInventoryQueries(productionType, options);
  const { can } = useRole();
  // Cost fields need skus.costs_manage on the server; sending one without it is a 403.
  const canWriteCosts = can('skus.costs_manage');

  // Get all mutations
  const {
    addStockMutation,
    setStockMutation,
    deductStockMutation,
    addSkuMutation,
    updateProductMutation,
    bulkAddStockMutation,
    bulkUpdateImageMutation,
    bulkDeleteMutation,
    bulkAddStockFromCsvMutation,
    moveToUnavailableMutation,
    moveToAvailableMutation,
  } = useInventoryMutations(products);

  // ========================================================================
  // DOWNLOAD STOCK CSV (Synchronous function)
  // ========================================================================
  const downloadStockCsv = async (items: Product[] = products) => {
    if (!selectedLocation) {
      toast({
        title: "Error",
        description: "No location selected",
        variant: "destructive",
      });
      return;
    }

    if (!items || items.length === 0) {
      toast({
        title: "No Data",
        description: "No stock data to download",
      });
      return;
    }

    try {
      inventoryApi.downloadStockCsv(items, selectedLocation.name);

      toast({
        title: "Download Complete",
        description: `Stock data downloaded successfully for ${selectedLocation.name}`,
      });
    } catch (err) {
      toast({
        title: "Download Failed",
        description: err instanceof Error ? err.message : "Unknown error occurred",
        variant: "destructive",
      });
    }
  };

  const failPrecondition = (title: string, description: string) => {
    toast({
      title,
      description,
      variant: "destructive",
    });
    return false;
  };

  /**
   * Shared submit path for both bulk-add flavours — a shared quantity applied
   * to every row (`bulkAddStock`) vs a per-row quantity (`bulkAddStockPerSku`).
   * One adjustment document (bulk_import_batches header) for the whole
   * submit; `reference` lands on the header.
   */
  const submitBulkAdd = async (
    rows: Array<{ id: string; quantity?: number }>,
    sharedQuantity: number,
    reference?: string,
    direction?: BulkStockDirection,
  ): Promise<boolean> => {
    try {
      if (!selectedLocation) {
        return failPrecondition('Bulk Add Stock Failed', 'No location selected');
      }

      const productById = new Map(products.map((p) => [p.id, p]));
      const productData: Array<{ id: string; sku: string; currentStock: number; quantity?: number }> = [];
      for (const row of rows) {
        const product = productById.get(row.id);
        if (!product) {
          return failPrecondition('Bulk Add Stock Failed', `Product not found: ${row.id}`);
        }
        productData.push({
          id: product.id,
          sku: product.sku,
          currentStock: product.stockLevel,
          ...(row.quantity != null ? { quantity: row.quantity } : {}),
        });
      }

      const data = await bulkAddStockMutation.mutateAsync({
        locationId: selectedLocation.id,
        productIds: productData,
        quantity: sharedQuantity,
        ...(reference?.trim() ? { reference: reference.trim() } : {}),
        ...(direction === 'consume' ? { direction } : {}),
      });
      // Partial failure resolves (applied rows are already on the document);
      // surface it as not-ok so the caller keeps its sheet open. The
      // mutation layer owns the partial toast.
      return data?.success !== false;
    } catch {
      return false;
    }
  };

  // ========================================================================
  // RETURN API (Matches old InventoryContext interface)
  // ========================================================================
  return {
    // Query state
    products,
    loading,
    isFetching,
    error: error as Error | null,
    selectedLocation,
    refreshInventory,

    // Mutations (wrapped to match old API)
    //
    // BLU-3xx Phase 47.01.2-B-04: addStock returns the
    // `inventory_transactions.id` from the v2 atomic RPC so callers (e.g.
    // AddStockModal → `useReceiveStockUndo`) can wire a 30-second Undo
    // toast that calls `inventory.reverseReceipt`.
    //
    // BLU-3xx Phase 47.01.2-B-fix3.1: takes a resolved `Product` (not a
    // sku string) so cross-org targets work end-to-end. See hook header.
    addStock: async (
      product: Product,
      quantity: number,
      receivedAt?: Date,
      locationIdOverride?: string,
      targetOrganizationId?: string | null,
      reference?: string,
    ): Promise<{ success: true; transactionId: string } | { success: false }> => {
      try {
        const locationId = locationIdOverride ?? selectedLocation?.id;
        if (!locationId) {
          failPrecondition('Add Stock Failed', 'No location selected');
          return { success: false };
        }

        const receivedNote = receivedAt
          ? `Manual stock addition (received ${receivedAt.toISOString().split('T')[0]})`
          : 'Manual stock addition';

        const result = await addStockMutation.mutateAsync({
          skuId: product.id,
          locationId,
          quantity,
          reason: receivedNote,
          // Operator reference (Thread Lab stock in/out log): rendered in
          // the Logs Order/Ref column as reference_type='adjustment_ref'.
          ...(reference?.trim() ? { reference: reference.trim() } : {}),
          // BLU-3xx Phase 47.01.2-B-04: optional cross-org target. Only set
          // when the caller has explicitly chosen one in the modal picker.
          ...(targetOrganizationId ? { targetOrganizationId } : {}),
        });
        return { success: true, transactionId: result.transactionId };
      } catch {
        return { success: false };
      }
    },

    setStock: async (
      product: Product,
      quantity: number,
      source?: string,
      _referenceType?: string,
      _referenceId?: string,
      notes?: string,
      locationIdOverride?: string,
      targetOrganizationId?: string | null,
      reference?: string,
    ): Promise<boolean> => {
      try {
        const locationId = locationIdOverride ?? selectedLocation?.id;
        if (!locationId) {
          return failPrecondition('Set Stock Failed', 'No location selected');
        }

        await setStockMutation.mutateAsync({
          skuId: product.id,
          locationId,
          quantity,
          reason: notes || (source === 'csv_import' ? 'Bulk stocktake from CSV' : 'Manual correction'),
          // Operator reference — see addStock.
          ...(reference?.trim() ? { reference: reference.trim() } : {}),
          ...(targetOrganizationId ? { targetOrganizationId } : {}),
        });
        return true;
      } catch {
        return false;
      }
    },

    deductStock: async (
      product: Product,
      quantity: number,
      source?: string,
      referenceType?: string,
      referenceId?: string,
      notes?: string
    ): Promise<boolean> => {
      try {
        if (!selectedLocation) {
          return failPrecondition('Deduct Stock Failed', 'No location selected');
        }

        await deductStockMutation.mutateAsync({
          skuId: product.id,
          locationId: selectedLocation.id,
          quantity,
          reason: notes,
          source,
          referenceType,
          referenceId,
        });
        return true;
      } catch {
        return false;
      }
    },

    addSku: async (newProduct: NewProduct): Promise<boolean> => {
      try {
        const locationId = newProduct.locationId ?? selectedLocation?.id;
        if (!locationId) {
          return failPrecondition('Add SKU Failed', 'No location selected');
        }

        await addSkuMutation.mutateAsync({
          locationId,
          sku: newProduct.sku,
          category: newProduct.category,
          stockLevel: newProduct.stockLevel,
          notify: newProduct.notify,
          imageUrl: newProduct.image_url || '',
          productionType: newProduct.isTrim ? undefined : newProduct.productionType ?? 'POD',
          skuCostOverride: canWriteCosts ? newProduct.skuCostOverride ?? undefined : undefined,
          isTrim: newProduct.isTrim,
        });
        return true;
      } catch {
        return false;
      }
    },

    updateProduct: async (product: Product, imageFile?: File): Promise<boolean> => {
      try {
        await updateProductMutation.mutateAsync({ product, imageFile });
        return true;
      } catch {
        return false;
      }
    },

    /**
     * Per-SKU bulk add (floating bulk-edit bar): each row carries its own
     * quantity. One adjustment document (bulk_import_batches header) for the
     * whole submit; `reference` lands on the header. The shared `quantity`
     * field is a required schema fallback — send 1; every row overrides it.
     */
    bulkAddStockPerSku: (
      rows: Array<{ id: string; quantity: number }>,
      reference?: string,
      direction?: BulkStockDirection,
    ): Promise<boolean> => submitBulkAdd(rows, 1, reference, direction),

    bulkAddStock: (productIds: string[], quantity: number, reference?: string): Promise<boolean> =>
      submitBulkAdd(productIds.map((id) => ({ id })), quantity, reference),

    bulkUpdateProductImage: async (productIds: string[], imageFile: File): Promise<boolean> => {
      try {
        await bulkUpdateImageMutation.mutateAsync({ productIds, imageFile });
        return true;
      } catch {
        return false;
      }
    },

    bulkDeleteProducts: async (productIds: string[]): Promise<boolean> => {
      try {
        if (!selectedLocation) {
          return failPrecondition('Delete Products Failed', 'No location selected');
        }

        await bulkDeleteMutation.mutateAsync({
          locationId: selectedLocation.id,
          productIds
        });
        return true;
      } catch {
        return false;
      }
    },

    bulkAddStockFromCsv: async (csvFile: File): Promise<ProcessResult> => {
      return await bulkAddStockFromCsvMutation.mutateAsync(csvFile);
    },

    moveToUnavailable: async (
      product: Product,
      quantity: number,
      reason?: string,
      targetOrganizationId?: string | null,
    ): Promise<boolean> => {
      try {
        if (!selectedLocation) {
          return failPrecondition('Update Unavailable Failed', 'No location selected');
        }

        await moveToUnavailableMutation.mutateAsync({
          locationId: selectedLocation.id,
          skuId: product.id,
          quantity,
          reason,
          ...(targetOrganizationId ? { targetOrganizationId } : {}),
        });
        return true;
      } catch {
        return false;
      }
    },

    moveToAvailable: async (
      product: Product,
      quantity: number,
      reason?: string,
      targetOrganizationId?: string | null,
    ): Promise<boolean> => {
      try {
        if (!selectedLocation) {
          return failPrecondition('Update Available Failed', 'No location selected');
        }

        await moveToAvailableMutation.mutateAsync({
          locationId: selectedLocation.id,
          skuId: product.id,
          quantity,
          reason,
          ...(targetOrganizationId ? { targetOrganizationId } : {}),
        });
        return true;
      } catch {
        return false;
      }
    },

    downloadStockCsv
  };
}
